import { getSanitizedDataLayerValue } from "../shared/DataLayerHelper";
import { SessionStorageKey } from "../shared/SessionStorageKeys";
import { Logger } from "../utils/Logger";
import { pushEventToDataLayer } from "../gtm/GtmUtils";
import { GtmEventNames } from "../gtm/GtmEventNames";
import { singleton } from "tsyringe";
import { DotCaPageSection } from "./enum/DotCaPageSection";

/**
 * Service to handle engagement tracking for DotCa
 */
@singleton()
export class DotCaEngagementService {
  private CurrentSection: string = "";
  private OhdioTimer: number | undefined = undefined;
  private IciTeleTimer: number | undefined = undefined;

  private readonly DotcaEngagementSettings = {
    pageViewThreshold: 2,
    timeSpentInSecondsThreshold: 30,
    refreshDelayMs: 2500,
  };

  /**
   * Handles the page view event.
   * Clears timers if the section has changed and triggers section page view handling.
   */
  handlePageView(): void {
    const pageSection = this.getCurrentSection();
    if (this.CurrentSection !== pageSection) {
      this.clearTimers();
    }
    this.CurrentSection = pageSection;

    if (this.CurrentSection === DotCaPageSection.Radio) {
      this.handlePageViewForSection(
        SessionStorageKey.OhdioEngagementData,
        SessionStorageKey.OhdioTriggeredEngagementEvent,
        GtmEventNames.OhdioEngagementPageView,
        GtmEventNames.OhdioEngagementTimeSpent,
        "OhdioTimer",
      );
    }
    if (this.CurrentSection === DotCaPageSection.Television) {
      this.handlePageViewForSection(
        SessionStorageKey.IciTeleEngagementData,
        SessionStorageKey.IciTeleTriggeredEngagementEvent,
        GtmEventNames.IciTeleEngagementPageView,
        GtmEventNames.IciTeleEngagementTimeSpent,
        "IciTeleTimer",
      );
    }
  }

  /**
   * Handles engagement tracking for page views.
   * Increments page view count and sets up a timer for tracking time spent.
   */
  private handlePageViewForSection(
    sessionDataKey: SessionStorageKey,
    triggeredEngagementEventKey: SessionStorageKey,
    pageViewEventName: GtmEventNames,
    timeSpentEventName: GtmEventNames,
    timerName: "OhdioTimer" | "IciTeleTimer",
  ): void {
    const { timeSpentInSecondsThreshold, pageViewThreshold } = this.DotcaEngagementSettings;
    const sessionData = this.getSessionData(sessionDataKey);
    sessionData.pageViewCount++;

    const shouldPushEvent =
      sessionData.pageViewCount >= pageViewThreshold && this.shouldTrackSectionEngagement(triggeredEngagementEventKey);
    if (shouldPushEvent) {
      this.pushEngagementEvent(pageViewEventName, triggeredEngagementEventKey);
      return;
    } else {
      sessionStorage.setItem(sessionDataKey, JSON.stringify(sessionData));
    }

    if (!this[timerName]) {
      this[timerName] = window.setInterval(
        () =>
          this.trackEngagementTime(
            timeSpentInSecondsThreshold,
            timeSpentEventName,
            sessionDataKey,
            triggeredEngagementEventKey,
          ),
        this.DotcaEngagementSettings.refreshDelayMs,
      );
    }
  }

  /**
   * Clears any active timers for tracking engagement.
   */
  private clearTimers(): void {
    if (this.OhdioTimer) {
      clearInterval(this.OhdioTimer);
      this.OhdioTimer = undefined;
    }
    if (this.IciTeleTimer) {
      clearInterval(this.IciTeleTimer);
      this.IciTeleTimer = undefined;
    }
  }

  /**
   * Retrieves the current section from the data layer.
   * @returns The current section.
   */
  private getCurrentSection(): string {
    return getSanitizedDataLayerValue("page.Section") ?? "";
  }

  /**
   * Retrieves session data for engagement tracking from session storage.
   * @param  storageKey key for the session storage item.
   * @returns The session data.
   */
  private getSessionData(storageKey: string): { pageViewCount: number; timeSpentInSeconds: number } {
    const defaultData = { pageViewCount: 0, timeSpentInSeconds: 0 };
    const data = sessionStorage.getItem(storageKey);
    if (!data) {
      return defaultData;
    }

    let parsedData;
    try {
      parsedData = JSON.parse(data);
    } catch {
      return defaultData;
    }

    if (
      parsedData &&
      typeof parsedData.pageViewCount === "number" &&
      typeof parsedData.timeSpentInSeconds === "number"
    ) {
      return parsedData;
    }

    return defaultData;
  }

  /**
   * Pushes an engagement event to the data layer.
   * @param eventName - The name of the event to push.
   * @param triggeredEngagementEventKey - The session storage key for the triggered engagement event.
   */
  private pushEngagementEvent(eventName: GtmEventNames, triggeredEngagementEventKey: SessionStorageKey): void {
    const hasViewedJeunesseContent = sessionStorage.getItem(SessionStorageKey.DotCaViewedJeunesseContent);
    Logger.verbose(`Triggering Engagement Event ${eventName}`);
    if (!hasViewedJeunesseContent) {
      pushEventToDataLayer(eventName);
      sessionStorage.setItem(triggeredEngagementEventKey, "true");
      this.clearTimers();
    }
  }

  /**
   * Determines if engagement tracking should be performed for the given section.
   * @returns True if engagement tracking should be performed, false otherwise.
   */
  private shouldTrackSectionEngagement(triggeredEngagementEventKey: SessionStorageKey): boolean {
    const hasViewedJeunesseContent = sessionStorage.getItem(SessionStorageKey.DotCaViewedJeunesseContent);
    if (hasViewedJeunesseContent) {
      Logger.verbose("Jeunesse content has been viewed, not tracking engagement");
      return false;
    }
    const hasSentEngagementData = sessionStorage.getItem(triggeredEngagementEventKey) === "true";
    if (hasSentEngagementData) {
      Logger.verbose(`Engagement event has been sent for ${this.CurrentSection} section, not tracking`);
      return false;
    }

    return true;
  }

  /**
   * Tracks the time spent on the current section and pushes an engagement event if the threshold is met.
   * @param timeSpentInSecondsThreshold - The threshold for time spent in seconds.
   * @param timeSpentEventName - The name of the time spent event to push.
   * @param engagementDataSessionStorageKey - The key for the session storage item.
   * @param triggeredEngagementEventKey - The session storage key for the triggered engagement event.
   */
  private trackEngagementTime(
    timeSpentInSecondsThreshold: number,
    timeSpentEventName: GtmEventNames,
    engagementDataSessionStorageKey: string,
    triggeredEngagementEventKey: SessionStorageKey,
  ): void {
    if (this.shouldTrackSectionEngagement(triggeredEngagementEventKey)) {
      const sessionData = this.getSessionData(engagementDataSessionStorageKey);
      sessionData.timeSpentInSeconds += this.DotcaEngagementSettings.refreshDelayMs / 1000;
      sessionStorage.setItem(engagementDataSessionStorageKey, JSON.stringify(sessionData));

      if (sessionData.timeSpentInSeconds >= timeSpentInSecondsThreshold) {
        this.pushEngagementEvent(timeSpentEventName, triggeredEngagementEventKey);
      }
    } else {
      this.clearTimers();
    }
  }
}
